import { useState } from "react";
import { MapPin, Navigation, Clock, ExternalLink } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";

const DEFAULTS = [
  { name: "Koumassi Remblais", address: "Koumassi Remblais, derrière la station PETROCI, Abidjan, Côte d'Ivoire", hours: "Lun. au sam. 8h00 à 18h00" },
  { name: "Port-Bouët Gonzagueville", address: "Port-Bouët Gonzagueville, Abidjan, Côte d'Ivoire", hours: "Lun. au sam. 8h00 à 18h00" },
];

const LocationsMap = () => {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const sites = (settings.locations || []).filter((l) => l.address);
  const list = sites.length ? sites : DEFAULTS;
  const [active, setActive] = useState(0);
  const site = list[active] || list[0];
  const q = encodeURIComponent(site.address);

  return (
    <section className="bg-white py-24" data-testid="maps-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead overline={t.maps.overline} title={t.maps.title} accent={t.maps.accent} sub={t.maps.sub} />
        <div className="mt-12 grid gap-6 lg:grid-cols-5">
          <div className="space-y-3 lg:col-span-2">
            {list.map((l, i) => (
              <Reveal key={i} delay={0.08 * i}>
                <button type="button" onClick={() => setActive(i)} data-testid={`maps-site-${i}`} aria-selected={active === i} className={`flex w-full flex-col items-start gap-2 rounded-3xl border p-6 text-left transition-all duration-300 ${active === i ? "border-gold bg-cream shadow-xl shadow-gold/10" : "border-brand/10 bg-white hover:border-brand/40"}`}>
                  <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold"><MapPin className="h-3.5 w-3.5" />{settings.company_name}</span>
                  <span className="font-display text-lg font-bold text-forest">{l.name}</span>
                  <span className="text-sm leading-relaxed text-brand-hover">{l.address}</span>
                  {l.hours && <span className="inline-flex items-center gap-1.5 text-xs text-brand"><Clock className="h-3.5 w-3.5" />{l.hours}</span>}
                </button>
              </Reveal>
            ))}
          </div>
          <Reveal className="lg:col-span-3" delay={0.15}>
            <div className="overflow-hidden rounded-3xl border border-brand/10 shadow-xl shadow-brand/5">
              <iframe key={site.address} title={site.name} src={`https://www.google.com/maps?q=${q}&z=15&output=embed`} className="h-[380px] w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen data-testid="maps-iframe" />
              <div className="flex flex-wrap items-center justify-between gap-3 bg-mint p-5">
                <p className="font-display text-base font-bold text-forest" data-testid="maps-active-name">{site.name}</p>
                <div className="flex flex-wrap gap-2">
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${q}`} target="_blank" rel="noopener noreferrer" data-testid="maps-directions" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-white shadow-lg shadow-gold/25 transition-all hover:-translate-y-0.5 hover:bg-gold-hover"><Navigation className="h-4 w-4" />{t.maps.directions}</a>
                  <a href={`https://www.google.com/maps/search/?api=1&query=${q}`} target="_blank" rel="noopener noreferrer" data-testid="maps-open" className="inline-flex items-center gap-2 rounded-full border border-brand/30 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-brand transition-colors hover:bg-white"><ExternalLink className="h-4 w-4" />{t.maps.open}</a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default LocationsMap;
